"use client";

import { format } from "date-fns";
import { useState } from "react";

/**
 * 本人確認書類と照合する項目と、就労可否判定の根拠になる国籍。
 * 変更すると本人確認書類の再提出が必要になる
 */
export type IdentityVerifiedFormValues = {
  lastName?: string;
  firstName?: string;
  lastNameKana?: string;
  firstNameKana?: string;
  sex?: string;
  nationality?: string;
  birthday?: unknown;
  zipCode?: string;
  prefecture?: string;
  city?: string;
  street?: string;
  building?: string;
};

const TEXT_FIELDS = [
  "lastName",
  "firstName",
  "lastNameKana",
  "firstNameKana",
  "sex",
  "nationality",
  "zipCode",
  "prefecture",
  "city",
  "street",
  "building",
] as const;

// 生年月日は日付だけを比べる。保存済みの値は "yyyy-MM-dd" の文字列、入力値は Date で渡る。
// 入力値はサーバーへ端末の日付で送られるため、端末の日付に直してから保存済みの日付と比べる
const toDateKey = (value: unknown) => {
  if (typeof value === "string") return value.slice(0, 10);
  return value instanceof Date && !Number.isNaN(value.getTime())
    ? format(value, "yyyy-MM-dd")
    : "";
};

/**
 * 保存済みのプロフィールと住所から、比較元の値を作る。
 * 生年月日はフォームの初期値（Date）ではなく、サーバーが保存している日付の文字列をそのまま使う。
 * 端末のタイムゾーンによって Date に直したときの日付がずれ、サーバーの判定と食い違わないようにするため
 */
export const buildIdentityVerifiedSavedValues = (
  profile?: {
    lastName: string;
    firstName: string;
    lastNameKana: string | null;
    firstNameKana: string | null;
    sex: string;
    nationality: string;
    birthday: string;
  } | null,
  address?: {
    postalCode: string;
    prefecture: string;
    city: string;
    street: string;
    building: string | null;
  },
): IdentityVerifiedFormValues => ({
  lastName: profile?.lastName,
  firstName: profile?.firstName,
  lastNameKana: profile?.lastNameKana ?? "",
  firstNameKana: profile?.firstNameKana ?? "",
  sex: profile?.sex,
  nationality: profile?.nationality,
  birthday: profile?.birthday,
  zipCode: address?.postalCode,
  prefecture: address?.prefecture,
  city: address?.city,
  street: address?.street,
  building: address?.building ?? "",
});

/** 本人確認書類と照合する項目のどれかが、保存済みの内容から変わっているか */
export const hasIdentityVerifiedFieldChanges = (
  before: IdentityVerifiedFormValues,
  after: IdentityVerifiedFormValues,
) =>
  TEXT_FIELDS.some((key) => (before[key] ?? "") !== (after[key] ?? "")) ||
  toDateKey(before.birthday) !== toDateKey(after.birthday);

/**
 * 本人確認が承認済みのワーカーが本人確認書類と照合する項目を変更したとき、保存前に確認を挟む。
 * 確認で保存を選んだときだけ保存し、保存したら本人確認書類の再提出が必要になる。
 *
 * 承認済みかどうかは保存するたびに取り直す。画面を開いたあとに承認されると、開いた時点の状態では
 * 確認を出さないまま保存され、サーバー側で本人確認済みが外れてしまうため
 */
export function useIdentityResubmissionConfirm<
  FORM_VALUES extends IdentityVerifiedFormValues,
>(
  save: (
    values: FORM_VALUES,
    options: { requiresResubmission: boolean },
  ) => Promise<void> | void,
  {
    fetchIsIdentityVerified,
    savedValues,
  }: {
    fetchIsIdentityVerified: () => Promise<boolean>;
    savedValues: IdentityVerifiedFormValues;
  },
) {
  const [pendingValues, setPendingValues] = useState<FORM_VALUES | null>(null);
  const [checking, setChecking] = useState(false);

  const isIdentityVerifiedNow = async () => {
    setChecking(true);
    try {
      return await fetchIsIdentityVerified();
    } finally {
      setChecking(false);
    }
  };

  const submit = async (values: FORM_VALUES) => {
    if (
      hasIdentityVerifiedFieldChanges(savedValues, values) &&
      (await isIdentityVerifiedNow())
    ) {
      setPendingValues(values);
      return;
    }

    await save(values, { requiresResubmission: false });
  };

  const confirm = async () => {
    const values = pendingValues;
    setPendingValues(null);
    if (values) await save(values, { requiresResubmission: true });
  };

  return {
    /** フォームの onSubmit から呼ぶ。確認が必要なときは保存せずダイアログを開く */
    submit,
    /** 確認ダイアログを開くかどうか */
    opened: pendingValues !== null,
    /**
     * 承認状態を取り直している最中か。国籍の確認などフォームの送信の外から呼ばれた場合も、
     * 取り直している間に保存ボタンをもう一度押せないようにするために使う
     */
    checking,
    /** 確認ダイアログで保存を選んだとき */
    confirm,
    /** 確認ダイアログでキャンセルを選んだとき */
    cancel: () => setPendingValues(null),
  };
}
